// Global Types
import type {
  $Translate,
} from 'common.language';

// Global Components
import * as React from 'react';
import ModalImage from 'react-modal-image';

// Helpers
import {
  isDiff,
} from '@vroskus/library-helpers/dist/browser';
import ErrorLog from '@vroskus/library-sentry/dist/browser';
import config from '../../../config';

// Components
import InputLabel from '../InputLabel';
import InputHidden from '../InputHidden';

// Types
import type {
  $InputProps,
} from '../../../types';

// Enums
const inputAccept = {
  image: [
    'image/png',
    'image/gif',
    'image/jpeg',
  ],
  pdf: [
    'application/pdf',
  ],
  word: [
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ],
};

type $OptionalProps = {
  className?: string;
  disabled?: boolean;
  image?: boolean;
  label?: string;
  labelClassName?: string;
  required?: boolean;
  value?: null | string;
};

type $Props = $InputProps & $OptionalProps & $Translate & {
  highlight: boolean;
  name: string;
};

type $State = {
  loading: boolean;
  value?: null | string;
};

class InputFileView extends React.Component<$Props, $State> {
  static defaultProps: $OptionalProps = {
    className: undefined,
    disabled: undefined,
    image: undefined,
    label: undefined,
    labelClassName: undefined,
    required: undefined,
    value: undefined,
  };

  constructor(props: $Props) {
    super(props);

    this.state = {
      loading: false,
      value: props.value,
    };
  }

  componentDidUpdate(prevProps: $Props): void {
    const {
      value,
    } = this.props;

    if (isDiff(
      prevProps.value,
      value,
    )) {
      this.setState({
        value,
      });
    }
  }

  async handleFile(type: 'file' | 'image', content: Blob): Promise<void> {
    const {
      setValue,
    } = this.props;

    try {
      await this.setStateAsync({
        loading: true,
      });

      const url = `${config.CDN_URL}/v1.0/upload/${type}`;
      const data = new FormData();

      data.append(
        'file',
        content,
      );

      const params = {
        body: data,
        'Content-Type': 'multipart/form-data',
        method: 'POST',
      };
      const response = await fetch(
        url,
        params,
      );
      const responseData = await response.json();

      const value = responseData.fileName;

      setValue(value);

      await this.setStateAsync({
        loading: false,
        value,
      });
    } catch (error) {
      await this.setStateAsync({
        loading: false,
      });

      ErrorLog.exception(error as Error);
    }
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  resetValue(): void {
    const {
      resetValue,
    } = this.props;

    resetValue();

    this.setState({
      value: null,
    });
  }

  renderResetButton() {
    const {
      t,
    } = this.props;

    return (
      <button
        className={'font-normal p-2 btn btn_light btn_outlined rounded-r-lg -mr-px upprecase'}
        onClick={() => this.resetValue()}
        type={'button'}
      >
        {t('Change file')}
      </button>
    );
  }

  renderInput() {
    const {
      disabled,
      image,
      name,
      required,
      t,
    } = this.props;

    const acceptStack: Array<string> = image
      ? inputAccept.image
      : [...inputAccept.pdf, ...inputAccept.word];

    return (
      <div className={'px-2'}>
        <input
          accept={acceptStack.join(',')}
          className={'Input opacity-0 w-full'}
          disabled={disabled}
          id={`_${name}`}
          name={`_${name}`}
          onChange={(e) => {
            if (e.target.files !== null) {
              this.handleFile(
                image ? 'image' : 'file',
                e.target.files[0],
              );
            }
          }}
          required={required}
          type={'file'}
        />
        <label
          className={'absolute font-normal top-0 right-0 -mt-px -mr-px btn btn_light btn_outlined rounded-r-lg upprecase'}
          htmlFor={`_${name}`}
        >
          {t('Choose')}
        </label>
      </div>
    );
  }

  renderImage() {
    const {
      value,
    } = this.state;

    return value ? (
      <ModalImage
        className={'w-10 h-10 my-px'}
        large={`${config.CDN_URL}/images/max/${value}`}
        small={`${config.CDN_URL}/images/sqr/${value}`}
      />
    ) : (
      <img
        alt={''}
        className={'w-10 h-10 my-px'}
        src={`${config.CDN_URL}/static/dummy.png`}
      />
    );
  }

  renderFile() {
    const {
      t,
    } = this.props;
    const {
      value,
    } = this.state;

    return (
      <div className={'py-2'}>
        <a
          href={`${config.CDN_URL}/files/${value || ''}`}
          rel={'noreferrer'}
          target={'_blank'}
        >
          {t('File')}
        </a>
      </div>
    );
  }

  renderContent() {
    const {
      image,
      name,
      required,
    } = this.props;
    const {
      value,
    } = this.state;

    return (
      <>
        {value ? (
          <div className={'flex flex-row flex-grow'}>
            <div className={'flex-1 px-2'}>
              {image ? this.renderImage() : this.renderFile()}
            </div>
            {this.renderResetButton()}
          </div>
        ) : this.renderInput()}
        <InputHidden
          name={name}
          required={required}
          value={value}
        />
      </>
    );
  }

  render() {
    const {
      className,
      highlight,
      isValid,
      label,
      labelClassName,
      name,
      required,
      t,
    } = this.props;
    const {
      loading,
    } = this.state;

    return (
      <div className={`InputFile ${className || ''}`}>
        <InputLabel
          className={labelClassName}
          label={label}
          name={name}
          required={required}
        />
        <div className={`InputFile-wrapper border-${highlight && !isValid ? 'red-600' : 'gray-400'} flex items-center text-sm sm:text-base placeholder-gray-600 rounded-lg border w-full focus:outline-none focus:border-blue-400 relative`}>
          {loading ? (
            <div className={'text-center w-full'}>
              {t('Loading...')}
            </div>
          ) : this.renderContent()}
        </div>
      </div>
    );
  }
}

export default InputFileView;
